import { LIBRARY_DISABLED_TYPES, randomId } from "@excalidraw/common";
import { deepCopyElement } from "@excalidraw/element";

import { CaptureUpdateAction } from "@excalidraw/element";

import { t } from "../i18n";

import { register } from "./register";

import type { BinaryFiles } from "../types";

export const actionAddToLibrary = register({
  name: "addToLibrary",
  trackEvent: { category: "element" },
  perform: (elements, appState, _, app) => {
    const selectedElements = app.scene.getSelectedElements({
      selectedElementIds: appState.selectedElementIds,
      includeBoundTextElement: true,
      includeElementsInFrames: true,
    });

    for (const type of LIBRARY_DISABLED_TYPES) {
      if (selectedElements.some((element) => element.type === type)) {
        return {
          captureUpdate: CaptureUpdateAction.EVENTUALLY,
          appState: {
            ...appState,
            errorMessage: t(`errors.libraryElementTypeError.${type}`),
          },
        };
      }
    }

    // Collect files from image elements
    const files: BinaryFiles = {};
    selectedElements.forEach((element) => {
      if (element.type === "image" && element.fileId) {
        const fileData = app.files[element.fileId];
        if (fileData) {
          files[element.fileId] = fileData;
        }
      }
    });

    return app.library
      .getLatestLibrary()
      .then((items) => {
        const libraryItem = {
          id: randomId(),
          status: "unpublished" as const,
          elements: selectedElements.map(deepCopyElement),
          created: Date.now(),
          ...(Object.keys(files).length > 0 && { files }),
        };

        // Immediately ensure files are available in app.files for persistence
        // This prevents the timing issue where files are removed from canvas
        // before the async library persistence cycle can save them
        if (Object.keys(files).length > 0) {
          Object.assign(app.files, files);

          // CRITICAL: Also save files to LocalData file storage system
          // This ensures files are available after page refresh
          // Library storage and LocalData file storage are separate systems
          import("../../../excalidraw-app/data/LocalData").then(
            async ({ LocalData }) => {
              const elementsWithImages = selectedElements.filter(
                (el) => el.type === "image",
              );
              if (elementsWithImages.length > 0) {
                await LocalData.fileStorage.saveFiles({
                  elements: elementsWithImages,
                  files,
                });
              }
            },
          );
        }

        return app.library.setLibrary([libraryItem, ...items]);
      })
      .then(() => {
        return {
          captureUpdate: CaptureUpdateAction.EVENTUALLY,
          appState: {
            ...appState,
            toast: { message: t("toast.addedToLibrary") },
          },
        };
      })
      .catch((error) => {
        return {
          captureUpdate: CaptureUpdateAction.EVENTUALLY,
          appState: {
            ...appState,
            errorMessage: error.message,
          },
        };
      });
  },
  label: "labels.addToLibrary",
});
